import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { BadgeDraft, BadgeView, RewardConfig, RewardSnapshot, RewardsExplainerView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { Trophy, Flame } from '@ui/shared/icons';
import { resolveLevelIdentity } from '@ui/shared/rewards/levelProgress';
import { XPSourcesTable } from './XPSourcesTable';
import { LevelArcList } from './LevelArcList';
import { BadgeGallery } from './BadgeGallery';
import { BadgeDetailSheet } from './BadgeDetailSheet';
import { BadgeEditorSheet } from './BadgeEditorSheet';

/**
 * "How rewards work" + reward configuration + badge gallery
 * (settings-rewards.md v2.2). Rewards transparency is a product requirement,
 * not a help page: every number the engine uses is on screen (§1), and
 * every number is editable (§1.3). This build's scope (CLAUDE.md /
 * phase-2a-rewards.md) is exactly Rewards v1: action-completion XP and
 * realism-bonus XP as the only two live sources; the rest of the XP table
 * and two starter-badge templates render correctly as "not yet active" /
 * locked-with-0-progress, which is the honest fresh-install state (§7), not
 * a bug.
 *
 * `ThemeSwitcher` no longer lives here (moved to `SettingsScreen`,
 * phase-2e-nav-settings-export.md §3/TS-1/TS-2, settings-rewards.md
 * §5a.4) — Rewards and Settings are now separate rail destinations, and the
 * switcher was always a provisional stand-in on this screen pending a real
 * Settings surface.
 *
 * Widened to the same 1600px shell as Dashboard/Objective Form/Today/
 * Weekly Review (Rewards/Settings desktop-consistency pass) and
 * restructured into that same two-column shape: XP sources + Level curve
 * as proper `--surface-raised` cards on the left (they previously had no
 * card background at all — the one screen in the app still using bare,
 * unstyled `<section>`s), a sticky "Your progress" sidebar on the right
 * (level identity/XP/streak — a glance summary that didn't exist as a
 * standalone element before, only implied by the highlighted row inside
 * `LevelArcList`), with the page-level "Edit values"/"Reset to defaults"
 * controls relocated into that sidebar since they govern BOTH left-column
 * sections, not just one. Badges gallery stays full-width below the
 * two-column split (a card grid reads better with the full 1600px than
 * squeezed into either column).
 */
export function RewardsScreenDesktop() {
  const api = useDomainApi();
  const version = useDomainVersion();

  const [snapshot, setSnapshot] = useState<RewardSnapshot | null>(null);
  const [explainer, setExplainer] = useState<RewardsExplainerView | null>(null);
  const [config, setConfig] = useState<RewardConfig | null>(null);
  const [badges, setBadges] = useState<readonly BadgeView[] | null>(null);

  const [editing, setEditing] = useState(false);
  const [draftConfig, setDraftConfig] = useState<RewardConfig | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const [selectedBadge, setSelectedBadge] = useState<BadgeView | null>(null);
  const [editingBadge, setEditingBadge] = useState<BadgeView | null>(null);
  const [creatingBadge, setCreatingBadge] = useState(false);

  const load = useCallback(async () => {
    const [snap, exp, cfg, badgeList] = await Promise.all([
      api.getRewardSnapshot(),
      api.getRewardsExplainer(),
      api.getRewardConfig(),
      api.listBadges(),
    ]);
    setSnapshot(snap);
    setExplainer(exp);
    setConfig(cfg);
    setBadges(badgeList);
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  // Refresh selectedBadge/editingBadge from the freshly-loaded list on every
  // domain change, so a sheet left open across an edit/archive shows current
  // state rather than a stale snapshot captured at click time.
  useEffect(() => {
    if (!badges) return;
    setSelectedBadge((prev) => (prev ? badges.find((b) => b.badgeId === prev.badgeId) ?? null : prev));
  }, [badges]);

  if (!snapshot || !explainer || !config || !badges) {
    return (
      <div style={{ padding: 'var(--space-6) var(--layout-page-margin-desktop) 0' }}>
        <SkeletonBlock height={32} width={220} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={200} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={200} />
      </div>
    );
  }

  const activeConfig = editing && draftConfig ? draftConfig : config;

  function startEditing() {
    setDraftConfig(config);
    setEditing(true);
  }

  async function commitDraft(next: RewardConfig) {
    setDraftConfig(next);
    // "Edits commit per control (no save button)" (§1.3) — each keystroke
    // that produces a structurally valid config is pushed to the domain
    // immediately; the domain layer's own assertValidRewardConfig guards
    // against a config that would corrupt level derivation (e.g. a
    // threshold edited to no longer be strictly increasing), so an
    // in-progress edit that's momentarily invalid (e.g. clearing a field to
    // type a new value) simply doesn't commit until it's valid again rather
    // than throwing mid-keystroke.
    try {
      await api.updateRewardConfig(next);
    } catch {
      // Invalid intermediate state (e.g. thresholds not yet strictly
      // increasing while editing) — swallowed here; draftConfig still
      // reflects what's in the input so the user can keep typing toward a
      // valid value. The engine's last-committed config is unaffected.
    }
  }

  async function resetToDefaults() {
    await api.resetRewardConfig();
    setResetConfirmOpen(false);
    setEditing(false);
    setDraftConfig(null);
  }

  return (
    <main aria-label="Rewards" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1600px', margin: '0 auto' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <Trophy size={22} color="var(--hue-primary-vivid)" aria-hidden="true" />
        <h1 style={{ margin: 0, fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)' }}>
          How rewards work
        </h1>
      </header>
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
        Every number the engine uses is on this page. No hidden math.
      </p>

      <div style={{ marginTop: 'var(--space-8)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--layout-desktop-panel-gap)', minWidth: 0 }}>
          <RewardsCard>
            <SectionHeading>XP sources</SectionHeading>
            {editing && (
              <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
                Changes recalculate your whole history instantly — past weeks, levels, and badges stay
                consistent. Nothing is lost.
              </p>
            )}
            <div style={{ marginTop: 'var(--space-3)' }}>
              <XPSourcesTable
                sources={explainer.xpSources}
                editing={editing}
                config={activeConfig}
                onChange={(next) => void commitDraft(next)}
              />
            </div>

            <ul style={{ marginTop: 'var(--space-4)', paddingLeft: 'var(--space-5)', color: 'var(--text-secondary)', fontSize: 'var(--type-caption-size)' }}>
              <li>Nothing subtracts. Ever. A miss, a lapse, or an undo never takes XP away — totals just recompute honestly.</li>
              <li>Study minutes are a record, not a payout — the completion tap already paid.</li>
              <li>Accepting a target or date adjustment pays like a completion, not less — recalibrating is the smart play.</li>
            </ul>
          </RewardsCard>

          <RewardsCard>
            <SectionHeading>Level curve</SectionHeading>
            <div style={{ marginTop: 'var(--space-3)' }}>
              <LevelArcList
                levels={explainer.levels}
                snapshot={snapshot}
                editing={editing}
                config={activeConfig}
                onChange={(next) => void commitDraft(next)}
              />
            </div>
          </RewardsCard>

          <RewardsCard>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionHeading>Badges</SectionHeading>
              <button
                type="button"
                onClick={() => setCreatingBadge(true)}
                style={{
                  color: 'var(--hue-primary)',
                  fontSize: 'var(--type-caption-size)',
                  fontWeight: 600,
                  minHeight: 'var(--touch-target-min)',
                }}
              >
                New badge
              </button>
            </div>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <BadgeGallery badges={badges} onSelect={setSelectedBadge} />
            </div>
          </RewardsCard>
        </div>

        <YourProgressPanel
          snapshot={snapshot}
          config={activeConfig}
          editing={editing}
          onToggleEditing={() => (editing ? setEditing(false) : startEditing())}
          onResetRequest={() => setResetConfirmOpen(true)}
        />
      </div>

      {resetConfirmOpen && (
        <ResetConfirmSheet
          onConfirm={() => void resetToDefaults()}
          onCancel={() => setResetConfirmOpen(false)}
        />
      )}

      {selectedBadge && (
        <BadgeDetailSheet
          badge={selectedBadge}
          onClose={() => setSelectedBadge(null)}
          onEdit={
            selectedBadge.isStarter
              ? undefined
              : () => {
                  setEditingBadge(selectedBadge);
                  setSelectedBadge(null);
                }
          }
          onArchive={
            selectedBadge.isStarter
              ? undefined
              : () => {
                  void api.archiveBadge(selectedBadge.badgeId).then(() => setSelectedBadge(null));
                }
          }
        />
      )}

      {editingBadge && (
        <BadgeEditorSheet
          initialName={editingBadge.name}
          initialIconId={editingBadge.iconId}
          initialCriterion={editingBadge.criterion}
          onSave={async (draft: BadgeDraft) => {
            await api.editBadge(editingBadge.badgeId, draft);
            setEditingBadge(null);
          }}
          onCancel={() => setEditingBadge(null)}
        />
      )}

      {creatingBadge && (
        <BadgeEditorSheet
          onSave={async (draft: BadgeDraft) => {
            await api.createBadge(draft);
            setCreatingBadge(false);
          }}
          onCancel={() => setCreatingBadge(false)}
        />
      )}
    </main>
  );
}

function RewardsCard({ children }: { readonly children: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--layout-card-padding-desktop)',
      }}
    >
      {children}
    </section>
  );
}

/**
 * Sticky "Your progress" sidebar (Rewards/Settings desktop-consistency
 * pass) — the glance summary this screen never had as a standalone
 * element (previously only implied by the highlighted row inside
 * `LevelArcList`). Also hosts the page-level "Edit values"/"Reset to
 * defaults" controls, relocated here since they govern both the XP
 * sources AND Level curve cards in the left column, not just one —
 * matching the Realism Engine sidebar's own role of hosting the
 * contextual actions for the whole screen. Purely presentational over
 * `snapshot`/`config` the screen already loaded; no new domain math.
 */
function YourProgressPanel({
  snapshot,
  config,
  editing,
  onToggleEditing,
  onResetRequest,
}: {
  readonly snapshot: RewardSnapshot;
  readonly config: RewardConfig;
  readonly editing: boolean;
  readonly onToggleEditing: () => void;
  readonly onResetRequest: () => void;
}) {
  const { identity, fractionToNext } = resolveLevelIdentity(snapshot, config);

  return (
    <aside
      aria-label="Your progress"
      style={{
        position: 'sticky',
        top: 'var(--space-8)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--layout-card-padding-desktop)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      <div>
        <span
          style={{
            fontSize: 'var(--type-label-size)',
            fontWeight: 'var(--type-label-weight)',
            letterSpacing: 'var(--type-label-tracking)',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          Your progress
        </span>
        <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--type-heading-size)', fontWeight: 700, color: 'var(--text-primary)' }}>
          Level {snapshot.level} · {identity.name}
        </p>
      </div>

      <div>
        <div
          className="num"
          style={{ fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)', lineHeight: 1 }}
        >
          {snapshot.xpTotal.toLocaleString()}
        </div>
        <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          total XP · {snapshot.xpIntoCurrentLevel} / {snapshot.xpRequiredForNextLevel} into this level
        </div>
        <div
          aria-hidden="true"
          style={{ marginTop: 'var(--space-2)', height: '6px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-hover)', overflow: 'hidden' }}
        >
          <div
            style={{
              width: `${Math.round(fractionToNext * 100)}%`,
              height: '100%',
              background: 'var(--hue-primary-vivid)',
              borderRadius: 'var(--radius-pill)',
            }}
          />
        </div>
      </div>

      {snapshot.streakDays > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Flame size={16} color="var(--state-caution)" aria-hidden="true" />
          <span style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-primary)', fontWeight: 600 }}>
            {snapshot.streakDays} day{snapshot.streakDays === 1 ? '' : 's'} streak
          </span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--border-subtle)' }}>
        <EditValuesToggle editing={editing} onToggle={onToggleEditing} />
        {editing && (
          <button
            type="button"
            onClick={onResetRequest}
            style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 500,
              minHeight: 'var(--touch-target-min)',
              textAlign: 'left',
            }}
          >
            Reset to defaults
          </button>
        )}
      </div>
    </aside>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return (
    <h2
      style={{
        margin: 0,
        fontSize: 'var(--type-heading-size)',
        fontWeight: 'var(--type-heading-weight)',
        color: 'var(--text-primary)',
      }}
    >
      {children}
    </h2>
  );
}

function EditValuesToggle({ editing, onToggle }: { readonly editing: boolean; readonly onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={editing}
      style={{
        color: 'var(--hue-primary)',
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        minHeight: 'var(--touch-target-min)',
      }}
    >
      {editing ? 'Done editing' : 'Edit values'}
    </button>
  );
}

function ResetConfirmSheet({ onConfirm, onCancel }: { readonly onConfirm: () => void; readonly onCancel: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Reset to defaults"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--surface-base) 70%, transparent)',
        padding: 'var(--space-6)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-overlay)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-2)',
          padding: 'var(--space-6)',
          maxWidth: '360px',
        }}
      >
        <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>
          Reset XP values and the level curve to defaults? Your history recalculates instantly —
          nothing is lost.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-5)' }}>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--hue-primary-vivid)',
              color: 'var(--text-on-accent)',
              fontWeight: 600,
              fontSize: 'var(--type-body-size)',
              border: '1px solid var(--hue-primary-strong)',
            }}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              fontSize: 'var(--type-body-size)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
